import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { HttpExtras } from '@lib/core';
import { EnvService } from '@lib/env';
import { map, Observable } from 'rxjs';
import { UriBuilder } from 'uribuilder';
import { FrameContractSelectionResponseDto } from '../model/frame-contract-selection-response.dto';

interface IProcurementRelationshipContractResponseDto {
    readonly id: number;
    readonly contractNumber: string;
    readonly description?: string | null;
    readonly details?: string | null;
    readonly validFrom: string;
    readonly reminderEnabled?: boolean | null;
    readonly reminderDays?: number | null;
    readonly cancellationText?: string | null;
    readonly contractValue?: number | null;
    readonly paymentType?: number | null;
    readonly paymentDayOfMonth?: number | null;
}

interface IProcurementRelationshipContractsResponseDto {
    readonly contracts: ReadonlyArray<IProcurementRelationshipContractResponseDto> | null;
}

@Injectable({
    providedIn: 'root',
})
export class FrameContractSelectionDataService {
    private readonly http = inject(HttpClient);
    private readonly env = inject(EnvService);

    public getFrameContracts(
        supplierCompanyId: string,
        companyCodeId?: string | null,
    ): Observable<FrameContractSelectionResponseDto[]> {
        if (!companyCodeId) {
            return this.getLegacyFrameContracts(supplierCompanyId);
        }

        const uri = this.env.getBase(
            'procurement-relationship',
            'customer',
            'suppliers',
            supplierCompanyId,
            'company-codes',
            companyCodeId,
            'contracts',
        );

        return this.http
            .get<IProcurementRelationshipContractsResponseDto>(uri.toString(), {
                params: HttpExtras.CUSTOM_ERROR_HANDLING,
            })
            .pipe(
                // BE already filters on VS_GENERAL_AGREEMENT = 1; no client-side filter needed.
                map((response) =>
                    (response.contracts ?? []).map((contract) => this.toFrameContractSelection(contract)),
                ),
            );
    }

    public getIsGeneralAgreementRequired(supplierCompanyId: string, companyCodeId: string): Observable<boolean> {
        const uri = this.env.getBase(
            'procurement-relationship',
            'customer',
            'suppliers',
            supplierCompanyId,
            'company-codes',
            companyCodeId,
            'general-agreement-required',
        );
        return this.http.get<boolean>(uri.toString());
    }

    private getLegacyFrameContracts(supplierCompanyId: string): Observable<FrameContractSelectionResponseDto[]> {
        const uri: UriBuilder = this.env.getBase('contracts', 'frame', 'current', 'supplier', supplierCompanyId);
        uri.query['onlyGeneralAgreements'] = 'true';

        return this.http.get<FrameContractSelectionResponseDto[]>(uri.toString());
    }

    private toFrameContractSelection(
        contract: IProcurementRelationshipContractResponseDto,
    ): FrameContractSelectionResponseDto {
        return {
            id: contract.id,
            contractNumber: contract.contractNumber,
            title: contract.description ?? '',
            description: contract.details ?? '',
            validFrom: contract.validFrom,
            reminderEnabled: contract.reminderEnabled ?? false,
            customerSupplierRelationId: 0,
            reminderDaysBefore: contract.reminderDays ?? 0,
            cancellationReason: contract.cancellationText ?? '',
            contractValue: contract.contractValue?.toString() ?? '',
            paymentType: contract.paymentType ?? 0,
            paymentDayOfMonth: contract.paymentDayOfMonth ?? 1,
            generalAgreement: true,
        };
    }
}
